import { Fragment, useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Icon from "components/Icon";
import Loader from "components/Loader";
import client from "libs/client";
import { getApiErrorMessage } from "libs/utils";
import type { CompetitionRowResponse, GenderEnum } from "libs/apiClient";
import { DatagridBackendFilterType } from "features/datagrid-configs/types";
import { isGranted } from "pages/productManagement/functions";
import { Permission } from "pages/productManagement/types";
import { CountryCode, CountryFlag, useToast } from "ui-kit";
import { CompetitionEditionsPanel } from "./CompetitionEditionsPanel";
import { CompetitionFormModal } from "./CompetitionFormModal";
import {
	COMPETITION_GENDER_OPTIONS,
	COMPETITION_TIER_OPTIONS,
	COMPETITION_TYPE_OPTIONS,
	competitionTierLabel,
	competitionTypeLabel,
	formatCompetitionFormat,
	INTERNATIONAL_COUNTRY_CODE,
	resolveCompetitionLogoUrl,
} from "./competitionOptions";
import "./competitions.css";

type CompetitionFilters = {
	search: string;
	seasonId: string;
	type: string;
	tier: string;
	country: string;
	gender: string;
};

const DEFAULT_FILTERS: CompetitionFilters = {
	search: "",
	seasonId: "",
	type: "",
	tier: "",
	country: "",
	gender: "",
};

function extractMutationErrorMessage(error: unknown): string {
	if (error && typeof error === "object") {
		const err = error as {
			json?: string | { message?: string };
			message?: string;
		};
		if (typeof err.json === "string") {
			return err.json;
		}
		if (err.json?.message) {
			return err.json.message;
		}
		if (err.message) {
			return err.message;
		}
	}
	return "Something went wrong";
}

function buildBackendFilters(filters: CompetitionFilters) {
	const backendFilters: Array<{ field: string; type: DatagridBackendFilterType; value: string }> = [
		{ field: "isArchived", type: DatagridBackendFilterType.Boolean, value: "false" },
	];

	const trimmedSearch = filters.search.trim();
	if (trimmedSearch !== "") {
		backendFilters.push({
			field: "name",
			type: DatagridBackendFilterType.Search,
			value: trimmedSearch,
		});
	}

	if (filters.type !== "") {
		backendFilters.push({
			field: "type",
			type: DatagridBackendFilterType.Exact,
			value: filters.type,
		});
	}

	if (filters.tier !== "") {
		backendFilters.push({
			field: "tier",
			type: DatagridBackendFilterType.Exact,
			value: filters.tier,
		});
	}

	if (filters.country !== "") {
		backendFilters.push({
			field: "country",
			type: DatagridBackendFilterType.Exact,
			value: filters.country,
		});
	}

	if (filters.gender !== "") {
		backendFilters.push({
			field: "gender",
			type: DatagridBackendFilterType.Exact,
			value: filters.gender,
		});
	}

	if (filters.seasonId !== "") {
		backendFilters.push({
			field: "seasonId",
			type: DatagridBackendFilterType.Exact,
			value: filters.seasonId,
		});
	}

	return backendFilters;
}

function countryLabel(code: string | undefined, labelsByCode: Record<string, string>): string {
	if (code == null || code === "") {
		return "-";
	}
	if (code === INTERNATIONAL_COUNTRY_CODE) {
		return "International";
	}
	return labelsByCode[code] ?? code;
}

function isCountryFlagCode(code: string | undefined): code is CountryCode {
	if (code == null || code === "" || code === INTERNATIONAL_COUNTRY_CODE) {
		return false;
	}
	return code.length === 2;
}

export function CompetitionsTable() {
	const queryClient = useQueryClient();
	const { addSuccessToast, addErrorToast } = useToast();
	const canCreate = isGranted("competition", Permission.CREATE);
	const canEdit = isGranted("competition", Permission.EDIT);
	const canDelete = isGranted("competition", Permission.DELETE);

	const [filters, setFilters] = useState<CompetitionFilters>(DEFAULT_FILTERS);
	const [searchDraft, setSearchDraft] = useState("");
	const [page, setPage] = useState(1);
	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [editingCompetition, setEditingCompetition] = useState<CompetitionRowResponse | null>(null);

	const { data: continentsResponse } = client.getContinentCountry.useQuery({ retry: false });
	const { data: seasonsResponse } = client.getSeasons.useQuery({ retry: false });

	const countryOptions = useMemo(() => {
		const options: Array<{ value: string; label: string }> = [
			{ value: INTERNATIONAL_COUNTRY_CODE, label: "International" },
		];
		const labelsByCode: Record<string, string> = {
			[INTERNATIONAL_COUNTRY_CODE]: "International",
		};

		for (const continent of continentsResponse?.data ?? []) {
			for (const country of continent.options ?? []) {
				options.push({ value: country.value, label: country.label });
				labelsByCode[country.value] = country.label;
			}
		}

		return { options, labelsByCode };
	}, [continentsResponse]);

	const seasonOptions = useMemo(() => {
		const seasons = seasonsResponse?.data?.items ?? [];
		return seasons
			.filter((season) => season.id != null && season.name != null && season.name !== "")
			.map((season) => ({
				value: String(season.id),
				label: season.name ?? String(season.id),
			}));
	}, [seasonsResponse]);

	const queryParams = useMemo(
		() => ({
			page,
			limit: 50,
			filters: buildBackendFilters(filters),
			order: { field: "id" as const, direction: "DESC" as const },
		}),
		[filters, page],
	);

	const { data: competitionsResponse, isLoading, isError, refetch } = client.getCompetitions.useQuery(
		queryParams,
		{ retry: false },
	);

	const patchVisibilityMutation = client.patchCompetitionScoutVisibility.useMutation();
	const archiveMutation = client.archiveCompetition.useMutation();

	const items = competitionsResponse?.data?.items ?? [];
	const pages = competitionsResponse?.data?.pages ?? 0;

	const invalidateCompetitions = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: ["getCompetitions"] });
	}, [queryClient]);

	const toggleExpanded = useCallback((competitionId: number) => {
		setExpandedIds((previous) => {
			const next = new Set(previous);
			if (next.has(competitionId)) {
				next.delete(competitionId);
			} else {
				next.add(competitionId);
			}
			return next;
		});
	}, []);

	const handleApplySearch = useCallback(() => {
		setPage(1);
		setFilters((previous) => ({ ...previous, search: searchDraft }));
	}, [searchDraft]);

	const handleFilterChange = useCallback((field: keyof CompetitionFilters, value: string) => {
		setPage(1);
		setFilters((previous) => ({ ...previous, [field]: value }));
	}, []);

	const handleToggleScoutVisibility = useCallback(
		async (competition: CompetitionRowResponse) => {
			if (competition.id == null || canEdit === false) {
				return;
			}

			try {
				await patchVisibilityMutation.mutateAsync({
					id: competition.id,
					body: { isScoutVisible: competition.isScoutVisible !== true },
				});
				addSuccessToast("Scout visibility updated");
				await invalidateCompetitions();
			} catch (error: unknown) {
				addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
			}
		},
		[addErrorToast, addSuccessToast, canEdit, invalidateCompetitions, patchVisibilityMutation],
	);

	const handleArchive = useCallback(
		async (competition: CompetitionRowResponse) => {
			if (competition.id == null || canDelete === false) {
				return;
			}

			const confirmed = window.confirm(
				`Archive "${competition.name ?? "this competition"}"? Its editions will be hidden from active views.`,
			);
			if (confirmed === false) {
				return;
			}

			try {
				await archiveMutation.mutateAsync({ id: competition.id });
				addSuccessToast("Competition archived");
				await invalidateCompetitions();
			} catch (error: unknown) {
				addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
			}
		},
		[addErrorToast, addSuccessToast, archiveMutation, canDelete, invalidateCompetitions],
	);

	return (
		<div className="competitions-page">
			<nav className="competitions-page__breadcrumbs" aria-label="Breadcrumb">
				<span>Leagues &amp; Tournaments</span>
				<span className="competitions-page__breadcrumbsPipe">/</span>
				<span className="competitions-page__breadcrumbsCurrent">Competitions</span>
			</nav>

			<div className="competitions-page__toolbar">
				<h1 className="competitions-page__title">Competitions</h1>
				{canCreate ? (
					<button type="button" className="btn btn-light-blue" onClick={() => setShowCreateModal(true)}>
						+ New competition
					</button>
				) : null}
			</div>

			<div className="competitions-page__filters">
				<form
					className="competitions-page__search"
					onSubmit={(event) => {
						event.preventDefault();
						handleApplySearch();
					}}
				>
					<span className="competitions-page__searchIcon" aria-hidden="true">
						<Icon icon="search" size={16} color="grey" />
					</span>
					<input
						type="text"
						value={searchDraft}
						placeholder="Search competitions..."
						onChange={(event) => setSearchDraft(event.target.value)}
					/>
				</form>

				<select
					value={filters.seasonId}
					onChange={(event) => handleFilterChange("seasonId", event.target.value)}
					aria-label="Season"
				>
					<option value="">All seasons</option>
					{seasonOptions.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>

				<select
					value={filters.type}
					onChange={(event) => handleFilterChange("type", event.target.value)}
					aria-label="Type"
				>
					<option value="">Type</option>
					{COMPETITION_TYPE_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>

				<select
					value={filters.tier}
					onChange={(event) => handleFilterChange("tier", event.target.value)}
					aria-label="Tier"
				>
					<option value="">Tier</option>
					{COMPETITION_TIER_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>

				<select
					value={filters.country}
					onChange={(event) => handleFilterChange("country", event.target.value)}
					aria-label="Country"
				>
					<option value="">Country</option>
					{countryOptions.options.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>

				<select
					value={filters.gender}
					onChange={(event) => handleFilterChange("gender", event.target.value)}
					aria-label="Gender"
				>
					<option value="">Gender</option>
					{COMPETITION_GENDER_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</div>

			{isLoading ? <Loader /> : null}

			{isError ? (
				<div className="competitions-page__error">
					<p>Failed to load competitions.</p>
					<button type="button" className="btn btn-light-blue" onClick={() => refetch()}>
						Retry
					</button>
				</div>
			) : null}

			{isLoading === false && isError === false ? (
				<div className="competitions-table-wrap">
					<table className="table competitions-table">
						<thead>
							<tr>
								<th aria-label="Expand" className="competitions-table__expandCol" />
								<th className="competitions-table__nameCol">Competition name</th>
								<th>Gender</th>
								<th>Competition ID</th>
								<th>Age category</th>
								<th>Country</th>
								<th>Tier / Region</th>
								<th className="competitions-table__typeCol">Type</th>
								<th className="competitions-table__formatCol">Format</th>
								<th>Editions</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{items.map((competition) => {
								const competitionId = competition.id;
								if (competitionId == null) {
									return null;
								}

								const isExpanded = expandedIds.has(competitionId);
								const isVisible = competition.isScoutVisible !== false;
								const countryCode = competition.country;
								const countryName = countryLabel(countryCode, countryOptions.labelsByCode);
								const tierRegionParts = [
									competitionTierLabel(competition.tier),
									competition.region,
								].filter((part) => part != null && part !== "" && part !== "-");
								const ageGroup =
									competition.ageGroup != null && competition.ageGroup !== ""
										? competition.ageGroup
										: null;
								const logoSrc = resolveCompetitionLogoUrl(competition.logoUrl);
								const typeLabel = competitionTypeLabel(competition.type);

								return (
									<Fragment key={competitionId}>
										<tr className={isExpanded ? "is-expanded" : undefined}>
											<td>
												<button
													type="button"
													className="competitions-expand-btn"
													onClick={() => toggleExpanded(competitionId)}
													aria-expanded={isExpanded}
													aria-label={isExpanded ? "Collapse editions" : "Expand editions"}
												>
													<span
														className={`icon icon-new icon-16 icon-dark-grey icon-chevron-right-grey competitions-expand-btn__icon ${
															isExpanded ? "is-expanded" : ""
														}`}
													/>
												</button>
											</td>
											<td className="competitions-table__nameCol">
												<div className="competitions-name-cell">
													{logoSrc !== "" ? (
														<img src={logoSrc} alt="" className="competitions-logo" />
													) : (
														<span className="competitions-logo competitions-logo--placeholder" />
													)}
													<span
														className="competitions-name-cell__name"
														title={competition.name ?? undefined}
													>
														{competition.name ?? "-"}
													</span>
												</div>
											</td>
											<td>
												<span className="competitions-badge competitions-badge--gender">
													{(competition.gender as GenderEnum | undefined) ?? "-"}
												</span>
											</td>
											<td className="competitions-table__mono">#{competitionId}</td>
											<td>
												{ageGroup != null ? (
													<span className="competitions-badge competitions-badge--muted">
														{ageGroup}
													</span>
												) : (
													"-"
												)}
											</td>
											<td>
												<div className="competitions-country-cell">
													{isCountryFlagCode(countryCode) ? (
														<CountryFlag code={countryCode.toLowerCase() as CountryCode} />
													) : null}
													<span>{countryName}</span>
												</div>
											</td>
											<td>{tierRegionParts.length > 0 ? tierRegionParts.join(" · ") : "-"}</td>
											<td className="competitions-table__typeCol">
												<span
													className="competitions-badge competitions-badge--muted"
													title={typeLabel}
												>
													{typeLabel}
												</span>
											</td>
											<td className="competitions-table__formatCol">
												{formatCompetitionFormat(
													competition.format,
													competition.matchLengthMinutes,
												)}
											</td>
											<td>{competition.editionsCount ?? 0}</td>
											<td>
												<div className="competitions-actions">
													{canEdit ? (
														<button
															type="button"
															className={`competitions-action-btn competitions-action-btn--scout ${
																isVisible ? "is-visible" : "is-hidden"
															}`}
															title={isVisible ? "Hide from scouts" : "Show to scouts"}
															onClick={() => handleToggleScoutVisibility(competition)}
														>
															<span
																className={`icon icon-new icon-eye ${
																	isVisible ? "icon-success" : "icon-danger"
																}`}
																aria-hidden="true"
															/>
														</button>
													) : (
														<span
															className={`competitions-action-btn competitions-action-btn--scout ${
																isVisible ? "is-visible" : "is-hidden"
															}`}
														>
															<span
																className={`icon icon-new icon-eye ${
																	isVisible ? "icon-success" : "icon-danger"
																}`}
																aria-hidden="true"
															/>
														</span>
													)}
													{canEdit ? (
														<button
															type="button"
															className="competitions-action-btn"
															title="Edit competition"
															onClick={() => setEditingCompetition(competition)}
														>
															<Icon icon="crayon" size={16} color="dark-grey" />
														</button>
													) : null}
													{canDelete ? (
														<button
															type="button"
															className="competitions-action-btn"
															title="Archive competition"
															onClick={() => handleArchive(competition)}
														>
															<Icon icon="trash-new" size={16} color="dark-grey" />
														</button>
													) : null}
												</div>
											</td>
										</tr>
										{isExpanded ? (
											<tr className="competitions-editions-row">
												<td colSpan={11}>
													<CompetitionEditionsPanel
														competitionId={competitionId}
														competitionName={competition.name ?? `Competition #${competitionId}`}
														hasGroups={competition.hasGroups === true}
														hasPhases={competition.hasPhases === true}
														seasonFilterId={
															filters.seasonId !== "" ? Number(filters.seasonId) : undefined
														}
													/>
												</td>
											</tr>
										) : null}
									</Fragment>
								);
							})}
						</tbody>
					</table>

					{items.length === 0 ? (
						<p className="competitions-page__empty">No competitions found.</p>
					) : null}

					{pages > 1 ? (
						<div className="competitions-pagination">
							<button
								type="button"
								className="btn"
								disabled={page <= 1}
								onClick={() => setPage((current) => Math.max(1, current - 1))}
							>
								Previous
							</button>
							<span>
								Page {page} of {pages}
							</span>
							<button
								type="button"
								className="btn"
								disabled={page >= pages}
								onClick={() => setPage((current) => current + 1)}
							>
								Next
							</button>
						</div>
					) : null}
				</div>
			) : null}

			{showCreateModal ? (
				<CompetitionFormModal
					onClose={() => setShowCreateModal(false)}
					onSaved={invalidateCompetitions}
				/>
			) : null}

			{editingCompetition != null ? (
				<CompetitionFormModal
					competition={editingCompetition}
					onClose={() => setEditingCompetition(null)}
					onSaved={invalidateCompetitions}
				/>
			) : null}
		</div>
	);
}

export default CompetitionsTable;
